import Mousetrap from 'mousetrap';
import { useEffect } from 'react';

const MODIFIER_KEYS = new Set(['ctrl', 'alt', 'shift', 'meta', 'option', 'command', 'mod', 'cmd']);

export function isMousetrapModifierKey(key: string): boolean {
  return MODIFIER_KEYS.has(key);
}

/**
 * Re-orders a Mousetrap combo string so modifiers come first and the real
 * key comes last. Guards against combos saved before the MousetrapRecord
 * sort fix (e.g. `"F10+alt+ctrl+shift"`), which mousetrap's own parser
 * mis-binds by silently dropping the real key (RP-5539).
 */
export function normalizeMousetrapCombo(combo: string): string {
  const tokens = combo.split('+');
  const modifiers = tokens.filter(isMousetrapModifierKey);
  const rest = tokens.filter((token) => !isMousetrapModifierKey(token));

  return [...modifiers, ...rest].join('+');
}

export function bindMousetrap(
  instance: Pick<typeof Mousetrap, 'bind'>,
  combo: string | string[],
  callback: (e: KeyboardEvent, combo: string) => void,
  action?: string
): void {
  const normalized = Array.isArray(combo)
    ? combo.map(normalizeMousetrapCombo)
    : normalizeMousetrapCombo(combo);
  if (action === undefined) {
    instance.bind(normalized, callback);
  } else {
    instance.bind(normalized, callback, action);
  }
}

export function unbindMousetrap(
  instance: Pick<typeof Mousetrap, 'unbind'>,
  combo: string | string[],
  action?: string
): void {
  const normalized = Array.isArray(combo)
    ? combo.map(normalizeMousetrapCombo)
    : normalizeMousetrapCombo(combo);
  if (action === undefined) {
    instance.unbind(normalized);
  } else {
    instance.unbind(normalized, action);
  }
}

/* View in fullscreen */
function openFullscreen() {
  document.documentElement?.requestFullscreen?.();
}

export default function initMousetraps() {
  // Mousetrap's built-in stopCallback has a `_belongsTo` check that
  // short-circuits when bound to `document`, so the INPUT/TEXTAREA guard
  // is never reached. Override to fix this.
  Mousetrap.prototype.stopCallback = function (_e: KeyboardEvent, element: Element) {
    const tagName = element.tagName;
    return (
      tagName === 'INPUT' ||
      tagName === 'SELECT' ||
      tagName === 'TEXTAREA' ||
      (element as HTMLElement).isContentEditable
    );
  };

  bindMousetrap(Mousetrap, 'shift+f', openFullscreen);
}

export const useMousetrap = () => {
  useEffect(() => {
    initMousetraps();
  }, []);
};
